/**
 * tokenStorage.ts – single source of truth for where the active session's
 * JWT/refresh-token live.
 *
 * The admin's real session always lives in `localStorage` (shared across
 * every tab of the origin, as before). A "View As User" / role preview
 * session is written to `sessionStorage` instead, which is scoped to the
 * single tab that started the preview. This keeps preview state from
 * leaking into other already-open tabs and, critically, from being
 * inherited by a brand-new tab (which starts with empty `sessionStorage`
 * and therefore always resolves back to the admin's real identity).
 *
 * Every read site for the "currently active" token/refresh-token should go
 * through `getActiveToken()` / `getActiveRefreshToken()` rather than
 * reading `localStorage`/`sessionStorage` directly, so preview-vs-real
 * resolution stays consistent everywhere (API calls, token decoding,
 * route guards, refresh scheduling, etc).
 */

import { jwtDecode } from 'jwt-decode';

export const TOKEN_KEY = '@token';
export const REFRESH_TOKEN_KEY = '@refreshToken';
export const PREVIEW_TOKEN_KEY = '@previewToken';
export const PREVIEW_REFRESH_TOKEN_KEY = '@previewRefreshToken';
export const PREVIEW_HOME_CHART_SCOPE_KEY =
  'home_kpi_chart_session_scope_preview_v1';
export const PREVIEW_INVENTORY_VIEW_MODE_KEY = 'inventory_view_mode_preview';

/** Whether this specific tab currently has an active preview session. */
export function isPreviewTabActive(): boolean {
  if (typeof window === 'undefined' || !window.sessionStorage) return false;
  return !!sessionStorage.getItem(PREVIEW_TOKEN_KEY);
}

/**
 * Whether the currently active token belongs to a role-preview ("View As
 * User") session, decoded from the JWT's own `is_preview` claim. Shared by
 * anything that needs to behave differently during preview — blocking
 * mutations (interceptor's guardPreviewMutations), suppressing audit
 * tracking (useActivityBeacon), etc — so the check stays in one place.
 */
export function isPreviewSession(): boolean {
  const token = getActiveToken();
  if (!token) return false;
  try {
    const decoded = jwtDecode<{ is_preview?: boolean }>(token);
    return Boolean(decoded?.is_preview);
  } catch {
    return false;
  }
}

/**
 * Returns the token that should be used for the current tab: the
 * tab-scoped preview token if one is active, otherwise the admin's real
 * (shared, localStorage) token.
 */
export function getActiveToken(): string | null {
  if (typeof window !== 'undefined' && window.sessionStorage) {
    const previewToken = sessionStorage.getItem(PREVIEW_TOKEN_KEY);
    if (previewToken) return previewToken;
  }
  return typeof window !== 'undefined' && window.localStorage
    ? localStorage.getItem(TOKEN_KEY)
    : null;
}

/** Refresh-token counterpart of {@link getActiveToken}. */
export function getActiveRefreshToken(): string | null {
  if (typeof window !== 'undefined' && window.sessionStorage) {
    const previewRefreshToken = sessionStorage.getItem(
      PREVIEW_REFRESH_TOKEN_KEY
    );
    if (previewRefreshToken) return previewRefreshToken;
  }
  return typeof window !== 'undefined' && window.localStorage
    ? localStorage.getItem(REFRESH_TOKEN_KEY)
    : null;
}

/** Persists the admin's real session. Shared across all tabs, as before. */
export function setRealSession(access: string, refresh?: string | null): void {
  localStorage.setItem(TOKEN_KEY, access);
  if (refresh) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  }
}

export function removeRealSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/**
 * Persists a preview session scoped to this tab only via sessionStorage.
 * Never touches the admin's real localStorage tokens.
 */
export function setPreviewSession(
  access: string,
  refresh?: string | null
): void {
  sessionStorage.setItem(PREVIEW_TOKEN_KEY, access);
  if (refresh) {
    sessionStorage.setItem(PREVIEW_REFRESH_TOKEN_KEY, refresh);
  }
}

/** Ends the tab-local preview session and clears its persisted store. Never touches localStorage. */
export function clearPreviewSession(): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  sessionStorage.removeItem(PREVIEW_TOKEN_KEY);
  sessionStorage.removeItem(PREVIEW_REFRESH_TOKEN_KEY);
  sessionStorage.removeItem('persist:root');
}
